import { useCallback } from 'react';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { isNullsy } from '~/_shared/utils/typeGuards';
import {
  defaultFillFilterHashPerBTGIdSelector,
  extraFillConfigPerBTGIdSelector, extraFillInfoPerFillIdSelector,
} from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFillSelectors';
import type { BTGWithActiveExtraFill } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { type AppState } from '../app.store';
import type { BoundaryTerritoryGroupId } from '../boundaries/boundaryIdentifier.type';

export const boundaryTerritoryGroupsSelector = (state: AppState) => state.map.boundaryTerritoryGroups.groups;
export const useBoundaryTerritoryGroupsSelector = () => useSelector(boundaryTerritoryGroupsSelector);

export const boundaryTerritoryGroupsWithActiveExtraFillSelector = createStateSelector(
  boundaryTerritoryGroupsSelector,
  extraFillConfigPerBTGIdSelector,
  defaultFillFilterHashPerBTGIdSelector,
  extraFillInfoPerFillIdSelector,
  (
    BTGs,
    extraFillDataPerBTG,
    defaultFillInfoPerBTGId,
    extraFillInfoPerFillId,
  ): ReadonlyArray<BTGWithActiveExtraFill> => {

    return BTGs.map(boundaryTerritoryGroup => {
      const extraFillData = extraFillDataPerBTG[boundaryTerritoryGroup.boundaryTerritoryGroupId];
      const activeFillId = extraFillData?.activeBoundaryFillId;
      const defaultFillFilterHash = defaultFillInfoPerBTGId[boundaryTerritoryGroup.boundaryTerritoryGroupId];
      const activeFillConfig = extraFillData?.extraFills?.find(fill => fill.boundaryFillId === activeFillId);
      const activeFillInfo: BTGWithActiveExtraFill['activeFillInfo'] = isNullsy(activeFillId) ? {
        filterHash: defaultFillFilterHash,
      } : extraFillInfoPerFillId[activeFillId];

      return {
        boundaryTerritoryGroup,
        activeFillConfig,
        activeFillInfo,
      };
    });
  });

export const useBoundaryTerritoryGroupsWithActiveExtraFillSelector = () =>
  useSelector(boundaryTerritoryGroupsWithActiveExtraFillSelector);

const boundaryTerritoryGroupWithActiveExtraFillSelector = createStateSelector(
  boundaryTerritoryGroupsWithActiveExtraFillSelector,
  (_state, btgId?: BoundaryTerritoryGroupId) => btgId,
  (btgInfos: ReadonlyArray<BTGWithActiveExtraFill>, btgId: BoundaryTerritoryGroupId) => {
    return btgInfos.find(btgInfo => btgInfo.boundaryTerritoryGroup.boundaryTerritoryGroupId === btgId);
  },
);

export const useBoundaryTerritoryGroupWithActiveExtraFillSelector = (btgId?: BoundaryTerritoryGroupId) =>
  useSelector(state => boundaryTerritoryGroupWithActiveExtraFillSelector(state, btgId));

const boundaryTerritoryGroupsIsLoadingSelector = (state: AppState) =>
  state.map.boundaryTerritoryGroups.isFetchLoading || state.map.boundaryTerritoryGroups.isActionLoading;
export const useBoundaryTerritoryGroupsIsLoadingSelector = () => useSelector(boundaryTerritoryGroupsIsLoadingSelector);

const findBoundaryTerritoryGroup = (boundaryTerritoryGroups: readonly BoundaryTerritoryGroup[], id?: BoundaryTerritoryGroupId | null) =>
  boundaryTerritoryGroups.find(btg => btg.boundaryTerritoryGroupId === id);

export const useGetBoundaryTerritoryGroup = () => {
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();

  const getBoundaryTerritoryGroupById = useCallback((id?: BoundaryTerritoryGroupId | null) =>
    findBoundaryTerritoryGroup(boundaryTerritoryGroups, id),
  [boundaryTerritoryGroups]);

  return {
    getBoundaryTerritoryGroupById,
  };
};
